import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  discoveryApiRef,
  fetchApiRef,
  useApi,
} from '@backstage/core-plugin-api';

/** A pull request in one of the reader's lists, as github-insights answers it. */
export type PullRequest = {
  number: number;
  title: string;
  repo?: string;
  author?: string;
  isDraft: boolean;
  htmlUrl: string;
  createdAt: string | null;
  updatedAt: string | null;
};

/** One list as github-insights answers it. */
export type ListAnswer = {
  total: number;
  pullRequests: PullRequest[];
  mapped: boolean;
  /** Organisations that could not be asked, so the list may be incomplete. */
  unreachable?: string[];
};

/** One list on the page: still asking, answered, or failed. */
export type ListState = {
  loading: boolean;
  error?: Error;
  data?: ListAnswer;
};

type Lists = {
  mine: ListState;
  reviews: ListState;
  /**
   * How many times something on the page has asked for the review list to be
   * shown — the open-reviews figure and the attention card's summary row
   * (specs/home-page.md AC-7). A count rather than a flag, so asking twice
   * opens the tab twice even if the reader switched back in between.
   */
  reviewsAsked: number;
  /** Opens the pull-request card on its review tab and brings it into view. */
  showReviews: () => void;
};

/** One list, fetched on its own so one failing leaves the other standing. */
const useList = (path: string, failure: string): ListState => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [state, setState] = useState<ListState>({ loading: true });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('github-insights');
        const res = await fetchApi.fetch(`${baseUrl}/${path}`);
        if (!res.ok) {
          throw new Error(`${failure} (${res.status} ${res.statusText})`);
        }
        const data = (await res.json()) as ListAnswer;
        if (active) setState({ loading: false, data });
      } catch (error) {
        if (active) setState({ loading: false, error: error as Error });
      }
    })();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi, path, failure]);

  return state;
};

const PullRequestListsContext = createContext<Lists | undefined>(undefined);

/**
 * Asks for the reader's two pull-request lists once, for everything on the home
 * page that shows them (DX-391): the counts row reads the same answers as the
 * pull-request card below it. Asked separately, the page sent four requests for
 * two answers, and a count on the row and the same count on a tab could — and
 * did — read them differently. The component page does the same with its
 * `ComponentPageContext`.
 */
export const PullRequestListsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const mine = useList(
    'my-open-pull-requests',
    'Failed to load your pull requests',
  );
  const reviews = useList(
    'review-requested',
    'Failed to load your review requests',
  );
  const [reviewsAsked, setReviewsAsked] = useState(0);
  const showReviews = useCallback(() => setReviewsAsked(n => n + 1), []);
  const lists = useMemo(
    () => ({ mine, reviews, reviewsAsked, showReviews }),
    [mine, reviews, reviewsAsked, showReviews],
  );
  return (
    <PullRequestListsContext.Provider value={lists}>
      {children}
    </PullRequestListsContext.Provider>
  );
};

/** The reader's two lists, from the `PullRequestListsProvider` around them. */
export const usePullRequestLists = (): Lists => {
  const lists = useContext(PullRequestListsContext);
  if (!lists) {
    throw new Error('usePullRequestLists needs a PullRequestListsProvider');
  }
  return lists;
};

/**
 * A count in one of its three states — a whole number, still being asked, or
 * not determined — never a 0 standing in for an answer that is not there
 * (specs/home-page.md AC-5). Every figure on the home page is one of these.
 */
export type Count = number | 'loading' | 'unknown';

/**
 * How many pull requests a list holds, as every figure on the page states it: a
 * whole count, 'loading', or 'unknown' — never a 0 standing in for an answer
 * that is not there (specs/home-page.md AC-5):
 *   - a request that failed or was refused is unknown, not empty;
 *   - no GitHub account resolved from the identity (`mapped: false`) means nobody
 *     asked the question, and a zero would tell a colleague nothing is waiting on
 *     them, which they have no way to know is false (AC-13);
 *   - an organisation that could not be asked makes the total a floor rather
 *     than a count (AC-21), and the list says which one.
 */
export const countOf = (state: ListState): Count => {
  if (state.loading) return 'loading';
  const { data, error } = state;
  if (error || !data || !data.mapped) return 'unknown';
  if ((data.unreachable?.length ?? 0) > 0) return 'unknown';
  return data.total;
};
